// 账号列表（/api/accounts）。
// - status / q 过滤走 URL search params
// - cursor 分页
// - 行内 NoteTagsCell（inline 编辑）→ POST /api/accounts/auth/{id}/metadata

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState, type FormEvent } from "react";
import { apiGet, apiPostForm, APIError } from "@/api/client";
import { API_ACCOUNTS, accountAuthMetadataPath } from "@/api/endpoints";
import type { AccountListResponse, AccountListEntry } from "@/api/types";
import { QueryView } from "@/components/ui/QueryView";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { StatusBadge, type StatusVariant } from "@/components/ui/StatusBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { InlineEditableCell } from "@/components/forms/InlineEditableCell";
import { AuthActionsCell } from "@/components/forms/AuthActionsCell";
import { AuthDeleteButton } from "@/components/forms/AuthDeleteButton";
import { toast } from "@/components/ui/Sonner";
import { useSession } from "@/hooks/useSession";
import { formatMs } from "@/lib/format";

export interface AccountsSearch {
  status?: string;
  q?: string;
  cursor?: string;
}

const AUTH_STATUS_OPTIONS = [
  { value: "all", label: "全部" },
  { value: "pending", label: "待审批" },
  { value: "verified", label: "已通过" },
  { value: "disabled", label: "已禁用" },
  { value: "revoked", label: "已吊销" },
] as const;
const AUTH_STATUS_VALUES: ReadonlySet<string> = new Set(
  AUTH_STATUS_OPTIONS.filter((option) => option.value !== "all").map(
    (option) => option.value,
  ),
);

function parseAccountsSearch(search: Record<string, unknown>): AccountsSearch {
  const pick = (v: unknown): string | undefined => {
    if (typeof v !== "string") return undefined;
    const t = v.trim();
    return t === "" ? undefined : t;
  };
  const status = pick(search.status);
  let cursor: string | undefined;
  if (search.cursor !== undefined && search.cursor !== null && search.cursor !== "") {
    const t = String(search.cursor).trim();
    if (t !== "") cursor = t;
  }
  return {
    status: status && AUTH_STATUS_VALUES.has(status) ? status : undefined,
    q: pick(search.q),
    cursor,
  };
}

export const Route = createFileRoute("/accounts/")({
  validateSearch: parseAccountsSearch,
  component: AccountsIndexRoute,
});

function AccountsIndexRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/accounts/" });
  return (
    <AccountsView
      search={search}
      onChange={(next) => navigate({ search: next, replace: false })}
    />
  );
}

function buildAccountsQuery(search: AccountsSearch): string {
  const params = new URLSearchParams();
  if (search.status) params.set("status", search.status);
  if (search.q) params.set("q", search.q);
  if (search.cursor) {
    params.set("cursor", search.cursor);
  }
  return params.toString();
}

function statusToVariant(status: string): StatusVariant {
  switch (status) {
    case "verified":
      return "ok";
    case "pending":
      return "warn";
    case "revoked":
      return "danger";
    default:
      return "neutral";
  }
}

function couponAmountText(value: number | null | undefined) {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "未采集";
}

function MemberBenefitSummary({ row }: { row: AccountListEntry }) {
  const benefit = row.member_benefit;
  if (!benefit) {
    return <span className="text-[hsl(var(--muted-foreground))]">未采集</span>;
  }
  const variant =
    benefit.status === "ok"
      ? "ok"
      : benefit.status === "partial"
        ? "warn"
        : benefit.status === "failed"
          ? "danger"
          : "neutral";
  const expire =
    typeof benefit.nearest_expiring_coupon_amount === "number" &&
    benefit.nearest_expiring_coupon_at
      ? `${benefit.nearest_expiring_coupon_amount}元 ${benefit.nearest_expiring_coupon_at}`
      : benefit.status === "ok"
        ? "暂无即将过期"
        : "未采集";
  return (
    <div className="min-w-[220px] space-y-1 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge variant={variant}>{benefit.status || "unknown"}</StatusBadge>
        <span className="font-mono">到期 {benefit.member_expire_at || "未采集"}</span>
      </div>
      <div className="font-mono text-[hsl(var(--muted-foreground))]">
        当前 {couponAmountText(benefit.owned_coupon_amount)} / 可领{" "}
        {couponAmountText(benefit.claimable_coupon_amount)}
      </div>
      <div className="font-mono text-[hsl(var(--muted-foreground))]">
        过期 {expire}
      </div>
      {benefit.error ? (
        <div className="max-w-[260px] truncate text-[hsl(var(--destructive))]">
          {benefit.error}
        </div>
      ) : null}
    </div>
  );
}

interface AccountsViewProps {
  search: AccountsSearch;
  onChange: (next: AccountsSearch) => void;
}

export function AccountsView({ search, onChange }: AccountsViewProps) {
  const queryClient = useQueryClient();
  const session = useSession();
  const canWrite = session.data?.can_write ?? false;
  const actor = session.data?.actor ?? "anonymous";
  const [form, setForm] = useState<AccountsSearch>(search);

  const q = useQuery({
    queryKey: ["accounts", search],
    queryFn: () =>
      apiGet<AccountListResponse>(`${API_ACCOUNTS}?${buildAccountsQuery(search)}`),
  });

  function apply(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    onChange({ ...form, cursor: undefined });
  }
  function reset() {
    setForm({});
    onChange({});
  }

  async function saveMetadata(authID: number, note: string, tags: string) {
    try {
      await apiPostForm(accountAuthMetadataPath(authID), { note, tags });
      toast.success("已保存");
      void queryClient.invalidateQueries({ queryKey: ["accounts"] });
    } catch (err) {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("保存失败", { description: msg });
      throw err;
    }
  }

  return (
    <div className="space-y-4">
      <form
        onSubmit={apply}
        aria-label="账号过滤器"
        className="grid grid-cols-1 gap-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 sm:grid-cols-4"
      >
        <label className="block text-xs">
          <span className="mb-1 block text-[hsl(var(--muted-foreground))]">
            status
          </span>
          <Select
            value={form.status ?? "all"}
            onValueChange={(value) =>
              setForm({ ...form, status: value === "all" ? undefined : value })
            }
          >
            <SelectTrigger aria-label="status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AUTH_STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="block text-xs sm:col-span-2">
          <span className="mb-1 block text-[hsl(var(--muted-foreground))]">
            搜索
          </span>
          <Input
            aria-label="q"
            placeholder="game_account / mac_id 子串"
            value={form.q ?? ""}
            onChange={(e) => setForm({ ...form, q: e.target.value || undefined })}
          />
        </label>
        <div className="flex items-end gap-2">
          <Button type="submit" size="sm">
            应用
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={reset}>
            重置
          </Button>
        </div>
      </form>

      <QueryView query={q} isEmpty={(d) => d.items.length === 0}>
        {(data) => (
          <>
            <Table dense>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>账号</TableHead>
                  <TableHead>mac_id</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>会员与充值券</TableHead>
                  <TableHead>备注</TableHead>
                  <TableHead>标签</TableHead>
                  <TableHead>最后出现</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((row: AccountListEntry, index) => {
                  const previous = index > 0 ? data.items[index - 1] : undefined;
                  const isFirstInGroup = previous?.subject_id !== row.subject_id;
                  return (
                    <Fragment key={row.id}>
                      {isFirstInGroup ? (
                        <TableRow
                          key={`group-${row.subject_id}`}
                          className="bg-[hsl(var(--muted))]/25 hover:bg-[hsl(var(--muted))]/35"
                        >
                          <TableCell
                            colSpan={9}
                            className="font-mono text-[hsl(var(--muted-foreground))]"
                          >
                            账号{" "}
                            <Link
                              to="/accounts/$accountId"
                              params={{ accountId: String(row.subject_id) }}
                              className="text-[hsl(var(--foreground))] hover:underline"
                            >
                              {row.subject}
                            </Link>{" "}
                            <span className="text-[hsl(var(--muted-foreground))]">
                              #{row.subject_id}
                            </span>
                          </TableCell>
                        </TableRow>
                      ) : null}
                      <TableRow key={row.id}>
                        <TableCell className="font-mono">
                          <Link
                            to="/accounts/$accountId"
                            params={{ accountId: String(row.subject_id) }}
                            className="hover:underline"
                          >
                            {row.subject_id}
                          </Link>
                        </TableCell>
                        <TableCell className="font-mono">
                          {isFirstInGroup ? (
                            <Link
                              to="/accounts/$accountId"
                              params={{ accountId: String(row.subject_id) }}
                              className="hover:underline"
                            >
                              {row.subject}
                            </Link>
                          ) : (
                            <span className="text-[hsl(var(--muted-foreground))]">
                              同账号
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono">{row.mac_id}</TableCell>
                        <TableCell>
                          <StatusBadge variant={statusToVariant(row.status)}>
                            {row.status}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>
                          {isFirstInGroup ? (
                            <MemberBenefitSummary row={row} />
                          ) : (
                            <span aria-hidden="true" />
                          )}
                        </TableCell>
                        <TableCell>
                          <InlineEditableCell
                            value={row.note ?? ""}
                            disabled={!canWrite}
                            ariaLabel={`note-${row.id}`}
                            onSave={(next) =>
                              saveMetadata(row.id, next, row.tags ?? "")
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <InlineEditableCell
                            value={row.tags ?? ""}
                            disabled={!canWrite}
                            ariaLabel={`tags-${row.id}`}
                            onSave={(next) =>
                              saveMetadata(row.id, row.note ?? "", next)
                            }
                          />
                        </TableCell>
                        <TableCell className="text-[hsl(var(--muted-foreground))]">
                          {row.last_seen_at ? formatMs(row.last_seen_at) : ""}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            <AuthActionsCell
                              domain="account"
                              authID={row.id}
                              status={row.status}
                              subject={row.subject}
                              actor={actor}
                              canWrite={canWrite}
                              onChanged={() =>
                                void queryClient.invalidateQueries({
                                  queryKey: ["accounts"],
                                })
                              }
                            />
                            <AuthDeleteButton
                              domain="account"
                              authID={row.id}
                              subject={row.subject}
                              actor={actor}
                              canWrite={canWrite}
                              onDeleted={() =>
                                void queryClient.invalidateQueries({
                                  queryKey: ["accounts"],
                                })
                              }
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
            {data.next_cursor ? (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onChange({ ...search, cursor: data.next_cursor })}
                >
                  下一页
                </Button>
              </div>
            ) : null}
          </>
        )}
      </QueryView>
    </div>
  );
}
